import type { GameEvent, GameState, PlayerId } from "@tanks/game-core";

export const PROTOCOL_VERSION = 1 as const;
export const MAX_CLIENT_MESSAGE_BYTES = 512;

export const INPUT_BITS = {
  forward: 1,
  backward: 2,
  left: 4,
  right: 8,
} as const;

export interface InputMessage {
  v: 1;
  type: "input";
  sequence: number;
  movement: number;
  /** Continuous joystick axes in world coordinates, normalized to [-1, 1]. */
  axisX?: number;
  axisY?: number;
  fire: boolean;
}

export interface RematchMessage {
  v: 1;
  type: "rematch";
  ready: boolean;
}

export interface PingMessage {
  v: 1;
  type: "ping";
  sentAt: number;
}

export type ClientMessage = InputMessage | RematchMessage | PingMessage;

export interface WelcomeMessage {
  v: 1;
  type: "welcome";
  playerId: PlayerId;
  state: GameState;
}

export interface SnapshotMessage {
  v: 1;
  type: "snapshot";
  acknowledgedSequence: number;
  state: GameState;
}

export interface EventMessage {
  v: 1;
  type: "event";
  event:
    | GameEvent
    | { type: "player-connected"; playerId: PlayerId }
    | { type: "player-disconnected"; playerId: PlayerId }
    | { type: "countdown" }
    | { type: "round-started"; round: number }
    | { type: "rematch-ready"; playerId: PlayerId; ready: boolean };
}

export interface PongMessage {
  v: 1;
  type: "pong";
  sentAt: number;
  serverTime: number;
}

export interface ErrorMessage {
  v: 1;
  type: "error";
  code: string;
  message: string;
}

export type ServerMessage =
  | WelcomeMessage
  | SnapshotMessage
  | EventMessage
  | PongMessage
  | ErrorMessage;

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function validBase(value: Record<string, unknown>): boolean {
  return value.v === PROTOCOL_VERSION && typeof value.type === "string";
}

export function parseClientMessage(raw: string): ClientMessage | null {
  if (new TextEncoder().encode(raw).byteLength > MAX_CLIENT_MESSAGE_BYTES) {
    return null;
  }
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isObject(value) || !validBase(value)) return null;

  if (value.type === "input") {
    if (
      typeof value.sequence !== "number" ||
      !Number.isSafeInteger(value.sequence) ||
      value.sequence < 0 ||
      typeof value.movement !== "number" ||
      !Number.isInteger(value.movement) ||
      value.movement < 0 ||
      value.movement > 15 ||
      (value.axisX !== undefined &&
        (typeof value.axisX !== "number" || !Number.isFinite(value.axisX) || value.axisX < -1 || value.axisX > 1)) ||
      (value.axisY !== undefined &&
        (typeof value.axisY !== "number" || !Number.isFinite(value.axisY) || value.axisY < -1 || value.axisY > 1)) ||
      typeof value.fire !== "boolean"
    ) {
      return null;
    }
    return {
      v: PROTOCOL_VERSION,
      type: "input",
      sequence: value.sequence,
      movement: value.movement,
      ...(value.axisX === undefined ? {} : { axisX: value.axisX }),
      ...(value.axisY === undefined ? {} : { axisY: value.axisY }),
      fire: value.fire,
    };
  }

  if (value.type === "rematch" && typeof value.ready === "boolean") {
    return {
      v: PROTOCOL_VERSION,
      type: "rematch",
      ready: value.ready,
    };
  }

  if (
    value.type === "ping" &&
    typeof value.sentAt === "number" &&
    Number.isFinite(value.sentAt)
  ) {
    return {
      v: PROTOCOL_VERSION,
      type: "ping",
      sentAt: value.sentAt,
    };
  }
  return null;
}

export function movementToInput(movement: number): {
  forward: boolean;
  backward: boolean;
  left: boolean;
  right: boolean;
} {
  return {
    forward: (movement & INPUT_BITS.forward) !== 0,
    backward: (movement & INPUT_BITS.backward) !== 0,
    left: (movement & INPUT_BITS.left) !== 0,
    right: (movement & INPUT_BITS.right) !== 0,
  };
}

export function encodeServerMessage(message: ServerMessage): string {
  return JSON.stringify(message);
}

export function isServerMessage(value: unknown): value is ServerMessage {
  if (!isObject(value) || !validBase(value)) return false;
  switch (value.type) {
    case "welcome":
      return (
        (value.playerId === 1 || value.playerId === 2) &&
        isObject(value.state)
      );
    case "snapshot":
      return (
        typeof value.acknowledgedSequence === "number" &&
        isObject(value.state)
      );
    case "event":
      return isObject(value.event);
    case "pong":
      return (
        typeof value.sentAt === "number" &&
        typeof value.serverTime === "number"
      );
    case "error":
      return typeof value.code === "string" && typeof value.message === "string";
    default:
      return false;
  }
}
